import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Compass, Sparkles, Coffee, Moon, Users, Shield, Gem, Banknote, MapPin, Loader2 } from 'lucide-react';
import TopBar from '@/components/TopBar';
import { useCity } from '@/context/CityContext';
import { getPlaces } from '@/lib/api';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'hidden_gems', label: 'Hidden gems',  query: 'tourism', icon: Gem,     img: 'https://images.unsplash.com/photo-1759888107146-b3c6e3584495?crop=entropy&cs=srgb&fm=jpg&q=80&w=800', tint: 'from-violet-500/40 to-indigo-700/30' },
  { id: 'food',        label: 'Food spots',    query: 'cafe',    icon: Coffee,  img: 'https://images.unsplash.com/photo-1703062379468-cfa96dcd3da7?crop=entropy&cs=srgb&fm=jpg&q=80&w=800', tint: 'from-amber-500/40 to-rose-700/30' },
  { id: 'free',        label: 'Free places',   query: 'park',    icon: Banknote, img: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=800&auto=format&fit=crop', tint: 'from-emerald-400/40 to-cyan-700/30' },
  { id: 'nightlife',   label: 'Nightlife',     query: 'bar',     icon: Moon,    img: 'https://images.unsplash.com/photo-1541532713592-79a0317b6b77?q=80&w=800&auto=format&fit=crop', tint: 'from-fuchsia-500/40 to-purple-800/30' },
  { id: 'family',      label: 'Family',        query: 'museum',  icon: Users,   img: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=800&auto=format&fit=crop', tint: 'from-sky-400/40 to-blue-700/30' },
  { id: 'safe_solo',   label: 'Solo-safe',     query: 'cafe',    icon: Shield,  img: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?q=80&w=800&auto=format&fit=crop', tint: 'from-cyan-400/40 to-teal-700/30' },
];

const FILTER_CHIPS = [
  { id: 'all',   label: 'All' },
  { id: 'open',  label: 'Open now' },
  { id: 'near',  label: 'Walking distance' },
  { id: 'top',   label: 'Top rated' },
];

export default function Explore() {
  const { city } = useCity();
  const [active, setActive] = useState('hidden_gems');
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const cat = CATEGORIES.find(c => c.id === active) || CATEGORIES[0];
    getPlaces(city.lat, city.lon, cat.query, 2500, 20)
      .then(r => { if (alive) setItems(r?.places || []); })
      .catch(() => { if (alive) toast.error('Could not load places'); setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [active, city.lat, city.lon]);

  const filtered = useMemo(() => {
    let arr = items.slice();
    if (filter === 'open') arr = arr.filter(p => p.open_now);
    if (filter === 'near') arr = arr.filter(p => (p.distance_m || 0) < 1200);
    if (filter === 'top')  arr = arr.filter(p => p.stars);
    return arr;
  }, [items, filter]);

  return (
    <div data-testid="explore-screen">
      <TopBar />

      <div className="px-5 pt-1">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#15291C] flex items-center justify-center shadow-[0_8px_22px_rgba(15,26,19,0.18)]">
            <Compass className="w-5 h-5 text-[#C4FF3D]" />
          </div>
          <div>
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#15291C]">Explore</p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F1A13]">Discover {city.name}</h1>
          </div>
        </div>
      </div>

      {/* Categories — bento card grid */}
      <div className="px-5 mt-5 grid grid-cols-2 gap-3" data-testid="explore-categories">
        {CATEGORIES.map((c, i) => {
          const Icon = c.icon;
          const isActive = active === c.id;
          return (
            <motion.button
              key={c.id}
              onClick={() => setActive(c.id)}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: i * 0.04 }}
              className={`relative h-32 rounded-3xl overflow-hidden text-left border ${isActive ? 'border-[#15291C]/35 shadow-[0_0_18px_rgba(196,255,61,0.40)]' : 'border-[#0F1A13]/10'} group`}
              data-testid={`explore-cat-${c.id}`}
            >
              <img src={c.img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
              <div className={`absolute inset-0 bg-gradient-to-br ${c.tint}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0F1A13]/90 via-[#0F1A13]/40 to-[#0F1A13]/10" />
              <div className="relative h-full p-3.5 flex flex-col justify-between">
                <span className="w-9 h-9 rounded-xl bg-white/15 border border-white/25 backdrop-blur-md flex items-center justify-center">
                  <Icon className="w-4 h-4 text-white" />
                </span>
                <p className="font-display text-[16px] font-bold text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">{c.label}</p>
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Filter chips */}
      <div className="mt-5 px-5 flex gap-2 overflow-x-auto no-scrollbar" data-testid="explore-filters">
        {FILTER_CHIPS.map(f => (
          <button
            key={f.id} onClick={() => setFilter(f.id)}
            className={`shrink-0 px-3.5 py-2 rounded-full text-[12.5px] font-semibold border transition-colors ${filter === f.id ? 'bg-[#C4FF3D]/30 border-[#15291C]/30 text-[#15291C]' : 'bg-[#0F1A13]/05 border-[#0F1A13]/10 text-[#4A5A50] hover:bg-[#0F1A13]/08'}`}
            data-testid={`explore-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Result list */}
      <div className="px-5 mt-4 flex flex-col gap-3 pb-2" data-testid="explore-results">
        {loading && (
          <>
            {[...Array(4)].map((_, i) => <div key={i} className="gt-skeleton h-20" />)}
          </>
        )}
        {!loading && filtered.length === 0 && (
          <div className="glass-card p-5 text-[#4A5A50] text-sm">
            <Sparkles className="w-4 h-4 text-[#15291C] inline mr-1.5" />
            No spots match yet — try another category or filter.
          </div>
        )}
        {!loading && filtered.map((p, i) => (
          <motion.div
            key={p.id || i}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
            className="glass-card glass-card-interactive p-4 flex items-start gap-3"
            data-testid={`explore-item-${i}`}
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C4FF3D]/35 to-[#C4FF3D]/15 border border-[#15291C]/15 flex items-center justify-center shrink-0">
              <MapPin className="w-4 h-4 text-[#15291C]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#0F1A13] font-semibold text-[14.5px] truncate">{p.name}</p>
              <p className="text-[#7A8A80] text-[12px] mt-0.5 truncate">{p.address || p.cuisine || p.brand || '—'}</p>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {typeof p.distance_m === 'number' && (
                  <span className="gt-pill gt-pill-light text-[10.5px]">{p.distance_m < 1000 ? `${p.distance_m}m` : `${(p.distance_m / 1000).toFixed(1)}km`}</span>
                )}
                {p.open_now && <span className="gt-pill gt-pill-mint text-[10.5px]">Open now</span>}
                {p.stars && <span className="gt-pill gt-pill-dark text-[10.5px]">{p.stars}★</span>}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
